import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { JsonPipe, NgTemplateOutlet } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  ElementRef,
  HostListener,
  input,
  model,
  output,
  QueryList,
  TemplateRef,
  ViewChildren,
} from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideGripVertical } from '@ng-icons/lucide';
import { HlmContextMenuImports } from '@spartan-ng/helm/context-menu';
import { HlmDropdownMenuImports } from '@spartan-ng/helm/dropdown-menu';

export interface OrderableItem {
  [key: string]: any;
}

export interface ReorderEvent<T> {
  item: T;
  fromIndex: number;
  toIndex: number;
  newOrder: T[];
}

export interface OrderedListTemplateContext<T> {
  $implicit: T;
  index: number;
  isSelected: boolean;
}

export interface OrderedListActions {
  label: string;
  shortcut?: string;
  enabled: (input?: any) => boolean;
  execute: (input?: any) => void;
}

@Component({
  selector: 'app-ordered-list',
  imports: [CdkDrag, CdkDropList, NgIcon, JsonPipe, NgTemplateOutlet, HlmDropdownMenuImports, HlmContextMenuImports],
  providers: [provideIcons({ lucideGripVertical })],
  templateUrl: './ordered-list.component.html',
  styleUrl: './ordered-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderedListComponent<T extends OrderableItem = OrderableItem> {
  @ViewChildren(CdkDrag, { read: ElementRef }) private dragElements!: QueryList<ElementRef>;

  readonly items = input.required<T[]>();

  readonly keyField = input<keyof T>('id' as keyof T);
  readonly orderField = input<keyof T>('indice' as keyof T);
  readonly showNumbers = input<boolean, boolean | string>(true, { transform: booleanAttribute });
  readonly showDragHandle = input<boolean, boolean | string>(true, { transform: booleanAttribute });
  readonly disabled = input<boolean, boolean | string>(false, { transform: booleanAttribute });
  readonly selectable = input<boolean, boolean | string>(false, { transform: booleanAttribute });
  readonly actionsGroups = input<OrderedListActions[][]>([], { alias: 'actions' });

  readonly selectedItem = model<T | null>(null);

  readonly itemTemplate = contentChild<TemplateRef<OrderedListTemplateContext<T>>>('itemTemplate');
  readonly actionsTemplate = contentChild<TemplateRef<{ $implicit: T }>>('actionsTemplate');
  readonly newItemTemplate = contentChild<TemplateRef<any>>('newItemTemplate');

  readonly itemReordered = output<ReorderEvent<T>>();
  readonly orderChanged = output<T[]>();
  readonly itemSelected = output<T | null>();

  readonly sortedItems = computed(() => {
    const items = this.items();
    const orderField = this.orderField();

    if (!items || items.length === 0) {
      return [];
    }

    return [...items].sort((a, b) => {
      const aOrder = a[orderField] as number;
      const bOrder = b[orderField] as number;

      if (aOrder === undefined && bOrder === undefined) return 0;
      if (aOrder === undefined) return 1;
      if (bOrder === undefined) return -1;

      return aOrder - bOrder;
    });
  });

  readonly selectedItemIndex = computed(() => {
    const selected = this.selectedItem();
    const items = this.sortedItems();
    if (!selected || items.length === 0) return -1;
    return items.findIndex((item) => this.getItemKey(item) === this.getItemKey(selected));
  });

  readonly canMoveUp = computed(() => {
    return this.selectedItemIndex() > 0;
  });

  readonly canMoveDown = computed(() => {
    const index = this.selectedItemIndex();
    const items = this.sortedItems();
    return index >= 0 && index < items.length - 1;
  });

  // Detect if macOS
  #ctrlOrCmd = navigator.userAgent.toLowerCase().includes('mac') ? '⌘' : 'Ctrl';

  readonly menuActions: OrderedListActions[] = [
    {
      label: 'Deplacer vers le haut',
      shortcut: `${this.#ctrlOrCmd}↑`,
      enabled: () => this.canMoveUp(),
      execute: () => this.moveItem(this.selectedItemIndex(), this.selectedItemIndex() - 1),
    },
    {
      label: 'Deplacer vers le bas',
      shortcut: `${this.#ctrlOrCmd}↓`,
      enabled: () => this.canMoveDown(),
      execute: () => this.moveItem(this.selectedItemIndex(), this.selectedItemIndex() + 1),
    },
  ];

  getItemKey(item: T) {
    return item[this.keyField()];
  }

  onDrop(event: CdkDragDrop<T[]>): void {
    if (this.disabled()) {
      return;
    }

    if (event.previousIndex === event.currentIndex) {
      return;
    }

    let newIndex = event.currentIndex;

    const enabledItems = [
      ...this.sortedItems().filter((x) => x[this.orderField()] !== undefined && x[this.orderField()] !== null),
    ];
    const disabledItems = [
      ...this.sortedItems().filter((x) => x[this.orderField()] === undefined || x[this.orderField()] === null),
    ];
    const movedItem = enabledItems[event.previousIndex];

    // Dans le cas où on déplace un élément actif après un élément désactivé
    if (event.currentIndex >= enabledItems.length) {
      newIndex = enabledItems.length - 1;
    }

    moveItemInArray(enabledItems, event.previousIndex, newIndex);
    const updatedItems = [...enabledItems, ...disabledItems].map((item, index) => {
      if (item[this.orderField()] === undefined) return item;
      return { ...item, [this.orderField()]: index };
    });

    const reorderEvent: ReorderEvent<T> = {
      item: movedItem,
      fromIndex: event.previousIndex,
      toIndex: newIndex,
      newOrder: updatedItems,
    };

    this.itemReordered.emit(reorderEvent);
    this.orderChanged.emit(updatedItems);
  }

  onItemClick(item: T, event?: Event): void {
    if (!this.selectable() || this.disabled()) {
      return;
    }

    const currentSelected = this.selectedItem();
    const newSelection = currentSelected && this.getItemKey(currentSelected) === this.getItemKey(item) ? null : item;

    if (!newSelection) {
      return;
    }
    this.selectedItem.set(newSelection);
    this.itemSelected.emit(newSelection);

    // Focus the newly selected item to enable keyboard shortcuts
    if (newSelection && event) {
      setTimeout(() => {
        const targetElement = event.target as HTMLElement;
        targetElement.focus();
      }, 0);
    }
  }

  isItemSelected(item: T): boolean {
    const selected = this.selectedItem();
    return selected ? this.getItemKey(selected) === this.getItemKey(item) : false;
  }

  trackByKey(_: number, item: T) {
    return this.getItemKey(item);
  }

  onContextmenuTrigger(item: T, event: MouseEvent) {
    this.onItemClick(item, event);
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (this.disabled() || !this.selectable() || !this.selectedItem()) {
      return;
    }

    const isCtrlOrCmd = event.ctrlKey || event.metaKey;

    if (isCtrlOrCmd && (event.key === 'ArrowUp' || event.key === 'ArrowDown') && !event.shiftKey) {
      const currentIndex = this.selectedItemIndex();

      if (currentIndex === -1) {
        return;
      }
      let targetIndex = -1;

      if (event.key === 'ArrowUp' && this.canMoveUp()) {
        targetIndex = currentIndex - 1;
        event.preventDefault();
        event.stopPropagation();
      } else if (event.key === 'ArrowDown' && this.canMoveDown()) {
        targetIndex = currentIndex + 1;
        event.preventDefault();
        event.stopPropagation();
      }
      this.moveItem(currentIndex, targetIndex);
    }
  }

  protected moveItem(fromIndex: number, toIndex: number): void {
    if (toIndex < 0 || toIndex >= this.sortedItems().length || fromIndex === toIndex) {
      return;
    }
    const items = [...this.sortedItems()];
    const movedItem = items[fromIndex];

    moveItemInArray(items, fromIndex, toIndex);
    const updatedItems = items.map((item, index) => ({
      ...item,
      [this.orderField()]: index,
    }));

    const reorderEvent: ReorderEvent<T> = {
      item: movedItem,
      fromIndex: fromIndex,
      toIndex: toIndex,
      newOrder: updatedItems,
    };

    this.itemReordered.emit(reorderEvent);
    this.orderChanged.emit(updatedItems);

    // Refocus the selected item after reordering to maintain keyboard event handling
    setTimeout(() => {
      this.focusSelectedItem();
    }, 0);
  }

  private focusSelectedItem(): void {
    const selectedIndex = this.selectedItemIndex();
    if (selectedIndex === -1) {
      return;
    }

    const dragElements = this.dragElements.toArray();
    if (dragElements[selectedIndex]) {
      dragElements[selectedIndex].nativeElement.focus();
    }
  }
}
